import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import { useCurrentUser } from '../../lib/currentUser';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { FUNCTIONS_OF_BEHAVIOR } from '../../lib/pbisConstants';

// Bundled long-form docs. Ship the markdown WITH the app so the
// "Full written docs" cards work regardless of whether the source
// repo is public or private — the previous version linked out to
// github.com/.../blob/main/docs/*.md, which 404s for anyone who
// isn't a collaborator on a private repo (that's what the customer
// caught with "Why is the Quickstart card not work?").
import quickStartDocMd from '../../../docs/QUICK_START_CARD.md?raw';
import userManualDocMd from '../../../docs/USER_MANUAL.md?raw';
import adminGuideDocMd from '../../../docs/ADMIN_GUIDE.md?raw';

const BUNDLED_DOCS = {
  quick_start: {
    key: 'quick_start',
    icon: 'Zap',
    title: 'Quick Start Card',
    subtitle: 'One-page reference for new teachers.',
    body: quickStartDocMd,
    githubPath: 'docs/QUICK_START_CARD.md',
  },
  user_manual: {
    key: 'user_manual',
    icon: 'BookOpen',
    title: 'User Manual',
    subtitle: 'Full teacher-facing manual with every workflow, FAQ, and glossary.',
    body: userManualDocMd,
    githubPath: 'docs/USER_MANUAL.md',
  },
  admin_guide: {
    key: 'admin_guide',
    icon: 'Settings',
    title: 'Admin & Onboarding',
    subtitle: 'Setup, deploy, teacher onboarding, FERPA/privacy, troubleshooting.',
    body: adminGuideDocMd,
    githubPath: 'docs/ADMIN_GUIDE.md',
  },
};

// Configure marked once. Docs are our own bundled files (trusted
// input at build time) so we don't need runtime HTML sanitization.
// GFM + linebreaks so the tables in QUICK_START_CARD.md render as
// tables rather than pipe-separated text.
marked.setOptions({ gfm: true, breaks: false });

// ---------------------------------------------------------------------------
// Data-tracking narrative: this app is a data-tracking platform for special
// education. Every workflow answers a question about student data. The Help
// page therefore leads with WHAT you track and WHY, then walks through the
// step-by-step HOW for each of the four core workflows.
// ---------------------------------------------------------------------------

const DATA_STREAMS = [
  {
    icon: 'UserCircle',
    color: '#2563eb',
    title: 'Student data',
    question: 'Who am I serving?',
    lives: 'Sidebar → Students → Student Profiles',
    examples: 'Demographics, contacts, accommodations, IEP dates',
  },
  {
    icon: 'Target',
    color: '#059669',
    title: 'IEP goal data',
    question: 'Is what we’re doing actually working?',
    lives: 'Sidebar → Students → IEP Goals & Objectives',
    examples: 'SMART goals with baseline, target, mastery, progress %',
  },
  {
    icon: 'AlertTriangle',
    color: '#dc2626',
    title: 'Behavior data (ABC + function)',
    question: 'What happened, when, and why?',
    lives: 'Sidebar → Students → Behavioral Incidents',
    examples:
      'Antecedent → Behavior → Consequence + hypothesized function, intensity, duration',
  },
  {
    icon: 'ClipboardList',
    color: '#7c3aed',
    title: 'Behavior support plan data',
    question: 'How is the team responding to the data?',
    lives: 'Inside each Student Profile → "Behavior Plan" tab',
    examples:
      'Target definition, hypothesis, replacement behavior, interventions, SMART goal, team roles, fidelity, escalation plan',
  },
];

const QUICK_STEPS = [
  {
    icon: 'AlertTriangle',
    color: '#dc2626',
    title: 'Log what happened',
    path: '/behavioral-incident-logging',
    ctaLabel: 'Open Log Behavior',
    seconds: 60,
    steps: [
      'Sidebar → Log Behavior.',
      'Tap one of the big colored buttons for what the student did.',
      'Pick one of the auto-filled descriptions (or edit it).',
      'Tap what happened right before, then what happened right after.',
      'Pick your best guess for why (the app suggests one for you).',
      'Everything else is optional — save when you\'re ready.',
    ],
  },
  {
    icon: 'Target',
    color: '#059669',
    title: 'Set a SMART goal',
    path: '/iep-goals-and-objectives-tracking',
    ctaLabel: 'Open IEP Goals',
    seconds: 30,
    steps: [
      'Sidebar → IEP Goals & Objectives → "Add Goal".',
      'Pick the student and the goal area.',
      'Specific: what will change?',
      'Measurable: frequency / duration / %?',
      'Baseline: where they start. Target: where they need to be.',
      'Mastery criteria: e.g. "3 consecutive weeks at 80%".',
      'Target date, then Save.',
    ],
  },
  {
    icon: 'ClipboardList',
    color: '#7c3aed',
    title: 'Build a Behavior Support Plan (BIP)',
    path: '/student-profile-management',
    ctaLabel: 'Open Student Profiles',
    seconds: 300,
    steps: [
      'Open a student’s profile → tap the "Behavior Plan" tab.',
      'If no plan exists, tap "Create Behavior Plan".',
      'Enter MTSS tier, operational target definition, FBA summary.',
      'Choose the dominant function → write a hypothesis statement.',
      'Enter a functionally-equivalent replacement behavior.',
      'Pick antecedent + consequence interventions, teaching strategies.',
      'Add a SMART goal (baseline, target, mastery, target date).',
      'Fill in progress monitoring, escalation plan, team roles, fidelity plan.',
      'Save. The plan is now the team’s shared source of truth.',
    ],
  },
  {
    icon: 'BarChart3',
    color: '#f59e0b',
    title: 'Review your data + patterns',
    path: '/behavioral-incident-logging',
    ctaLabel: 'Open Analytics',
    seconds: 60,
    steps: [
      'Sidebar → Behavioral Incidents (scroll to Analytics section).',
      'Read the insight cards for dominant function, top antecedent, peak time, intensity trend.',
      'Switch chart tabs: Function pie, Antecedent bar, Time-of-day bar.',
      'For meeting-ready reports: Sidebar → Analytics → Reports & Analytics.',
      'For AI-detected patterns: Sidebar → Analytics → AI Insights.',
      'Take one insight into your next team meeting.',
    ],
  },
];

const FAQ_ITEMS = [
  {
    q: 'I opened the app and it looks totally different — what happened?',
    a: 'Nothing was removed. The sidebar has all the same pages you had before, plus new ones (PBIS Resources, Help & Manual, Behavior Plan tab). Tap the ☰ hamburger menu in the top-left corner to open the sidebar and pick any page.',
  },
  {
    q: 'I don’t know what "hypothesized function" to pick.',
    a: 'That’s fine — pick "unknown" for the first 5–10 incidents. Then check the Analytics section — a dominant function usually emerges from the pattern. You can edit the incident later to update the function once you’re sure.',
  },
  {
    q: 'What’s the difference between an IEP Goal and a Behavior Plan?',
    a: 'A Behavior Plan (BIP) is the full comprehensive plan — hypothesis, replacement behavior, interventions, team, escalation. An IEP Goal is one measurable target inside the plan. A BIP usually contains 1–3 SMART goals.',
  },
  {
    q: 'I made a mistake in an incident. How do I edit it?',
    a: 'Open the incident from the timeline → tap the Edit (pencil) icon → fix the fields → Save. To remove entirely, tap Delete (trash icon) → confirm.',
  },
  {
    q: 'It says "Demo mode" at the top of the incidents page. Is that bad?',
    a: 'Demo mode means your data is only saved locally in your browser, not shared with the team. If you’re supposed to be in Live mode, tell your admin — the cloud database environment variables likely need to be set. In Demo mode you can still practice; just know nothing syncs.',
  },
  {
    q: 'Do I have to log every single incident?',
    a: 'Log any incident that (a) required an intervention, (b) is a pattern you want to track, or (c) your BIP calls for. Trivial one-offs may not be worth the time. Consistency beats completeness — 100 well-logged incidents are worth more than 500 rushed ones.',
  },
  {
    q: 'Same behavior in two students — do I use the same intervention?',
    a: 'Not necessarily. If Student A is throwing a chair for attention and Student B is throwing a chair to escape a hard task, the interventions should be opposite. Match the intervention to the function, not to the topography of the behavior.',
  },
  {
    q: 'Where can I find the printable Quick Start Card?',
    a: 'Right here on this page — scroll to the "Printable Quick Start Card" section below and use the Print button. It fits on one page and covers everything a new teacher needs in their first week.',
  },
];

const GLOSSARY = [
  { term: 'ABC data', def: 'Antecedent-Behavior-Consequence data. The three-part structure for describing any incident.' },
  { term: 'Antecedent', def: 'What happened immediately before the behavior. Setting, task, prompt, denial, transition, peer interaction, etc.' },
  { term: 'BIP / BSP', def: 'Behavior Intervention Plan / Behavior Support Plan. The plan the team writes to address a target behavior.' },
  { term: 'CICO', def: 'Check-In / Check-Out. A common Tier 2 intervention where the student meets briefly with a trusted adult at start and end of day.' },
  { term: 'Consequence', def: 'What happened immediately after the behavior. Technical term meaning "response," not "punishment."' },
  { term: 'Duration', def: 'How long the behavior lasted, in seconds.' },
  { term: 'FBA', def: 'Functional Behavior Assessment. The process of collecting data to identify the function of a behavior.' },
  { term: 'Fidelity', def: 'Whether the plan is actually being implemented as written.' },
  { term: 'Function of behavior', def: 'Why the behavior is happening. Standard categories: attention, escape, tangible, sensory, automatic, unknown.' },
  { term: 'Hypothesis statement', def: '"When [antecedent], [student] does [behavior], in order to [function]."' },
  { term: 'IEP', def: 'Individualized Education Program.' },
  { term: 'Intensity', def: 'How severe the behavior was, on a 1–5 scale.' },
  { term: 'MTSS', def: 'Multi-Tiered System of Supports. Three tiers of increasing support intensity.' },
  { term: 'Operational definition', def: 'A behavior description specific enough that any two staff would identify the same events.' },
  { term: 'PBIS', def: 'Positive Behavioral Interventions and Supports. Framework for schoolwide behavior support (pbis.org).' },
  { term: 'Progress %', def: '0–100 slider on each IEP goal showing how close the student is to mastery.' },
  { term: 'Replacement behavior', def: 'A socially acceptable behavior that serves the same function as the target behavior.' },
  { term: 'SMART goal', def: 'Specific, Measurable, Achievable, Relevant, Time-bound.' },
  { term: 'Tier 1 / 2 / 3', def: 'Universal (all students) / Targeted (~15%) / Intensive (~5%) supports.' },
];

const SIDEBAR_MAP = [
  { icon: 'LayoutDashboard', where: 'Dashboard', for_: 'Overview of your caseload' },
  { icon: 'UserCircle', where: 'Students → Student Profiles', for_: 'Add/edit students, Behavior Plan tab' },
  { icon: 'Target', where: 'Students → IEP Goals', for_: 'SMART goals + progress %' },
  { icon: 'AlertTriangle', where: 'Students → Behavioral Incidents', for_: 'ABC data + function of behavior' },
  { icon: 'FileText', where: 'Analytics → Reports & Analytics', for_: 'Printable reports for meetings' },
  { icon: 'Brain', where: 'Analytics → AI Insights', for_: 'Automated pattern detection' },
  { icon: 'MessageSquare', where: 'Collaboration → Multi-User Hub', for_: 'Team chat + shared notes' },
  { icon: 'BookOpen', where: 'PBIS Resources', for_: 'Framework reference (no data here)' },
  { icon: 'HelpCircle', where: 'Help & Manual', for_: 'This page — manual, glossary, FAQ' },
];

const HelpAndManualPage = () => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isRealOwner = currentUser?.backend === 'supabase';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  // Which bundled doc, if any, is currently open in the inline
  // viewer. Keys: 'quick_start' | 'user_manual' | 'admin_guide'.
  const [openDoc, setOpenDoc] = useState(null);
  const docCloseRef = useRef(null);

  const openDocMeta = openDoc ? BUNDLED_DOCS[openDoc] : null;
  const openDocHtml = useMemo(() => {
    if (!openDocMeta) return '';
    try {
      return marked.parse(openDocMeta.body || '');
    } catch (err) {
      console.error('Failed to render bundled doc:', err);
      return `<pre>${(openDocMeta.body || '').replace(/[<>&]/g, (c) => (
        c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;'
      ))}</pre>`;
    }
  }, [openDocMeta]);

  useEffect(() => {
    if (!openDoc) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenDoc(null);
    };
    document.addEventListener('keydown', onKey);
    // Move focus to the close button when a doc opens so keyboard /
    // screen-reader users don't have to hunt for it.
    if (docCloseRef.current) {
      try { docCloseRef.current.focus(); } catch { /* noop */ }
    }
    // Lock body scroll while the viewer is open — the doc itself
    // scrolls inside its own panel.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [openDoc]);

  const glossaryFiltered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return GLOSSARY;
    return GLOSSARY.filter(
      (item) => item.term.toLowerCase().includes(needle) || item.def.toLowerCase().includes(needle),
    );
  }, [search]);

  const handlePrint = () => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16 print:ml-0 print:pt-0`}
      >
        <div className="p-6 max-w-6xl mx-auto space-y-8 print:p-0 print:space-y-4 print:max-w-full">
          <div className="print:hidden">
            <Breadcrumb
              customItems={[
                { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                { label: 'Help & Manual', path: null, icon: 'HelpCircle', current: true },
              ]}
            />
          </div>

          {/* Hero -------------------------------------------------------- */}
          <div className="print:hidden">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[16rem]">
                <h1 className="text-3xl font-bold text-foreground">Help & Manual</h1>
                <p className="text-muted-foreground mt-2 max-w-3xl">
                  EduTracker Pro is a <strong>data-tracking platform for special
                  education</strong>. Every feature exists to help you collect, review, and
                  act on data about the students you serve. This page is your reference
                  when something in the app is unfamiliar.
                </p>
              </div>
              <Button variant="outline" onClick={handlePrint} className="print:hidden">
                <Icon name="Printer" size={16} />
                Print Quick Start Card
              </Button>
            </div>
          </div>

          {/* What you track --------------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6 print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Database" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                What you're tracking (and why)
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Four data streams. Everything in the app is a way to enter, view, or analyze one
              of these four. Keeping data in its correct home is what turns the app from a
              stack of forms into evidence for data-based decisions.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {DATA_STREAMS.map((stream) => (
                <div
                  key={stream.title}
                  className="rounded-lg border p-4"
                  style={{
                    borderColor: `${stream.color}55`,
                    backgroundColor: `${stream.color}0d`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name={stream.icon} size={20} style={{ color: stream.color }} />
                    <p className="font-semibold text-card-foreground">{stream.title}</p>
                  </div>
                  <p className="text-sm italic text-muted-foreground mb-2">
                    "{stream.question}"
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Where it lives:</span>{' '}
                    {stream.lives}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-medium text-foreground">Examples:</span>{' '}
                    {stream.examples}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Sidebar map ------------------------------------------------ */}
          <section className="bg-card border border-border rounded-lg p-6 print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Map" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                The sidebar — your map of the app
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              On phone/tablet the sidebar is hidden by default. Tap the <strong>☰
              hamburger icon</strong> in the top-left corner of any page to open it.
            </p>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 w-10">&nbsp;</th>
                    <th className="text-left px-3 py-2">Where</th>
                    <th className="text-left px-3 py-2">For</th>
                  </tr>
                </thead>
                <tbody>
                  {SIDEBAR_MAP.map((row) => (
                    <tr key={row.where} className="border-t border-border">
                      <td className="px-3 py-2">
                        <Icon name={row.icon} size={16} className="text-primary" />
                      </td>
                      <td className="px-3 py-2 font-medium text-card-foreground">{row.where}</td>
                      <td className="px-3 py-2 text-muted-foreground">{row.for_}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quick-step walkthroughs ------------------------------------ */}
          <section className="print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Zap" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-foreground">Step-by-step walkthroughs</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {QUICK_STEPS.map((quick) => (
                <div
                  key={quick.title}
                  className="rounded-lg border border-border bg-card p-5 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={quick.icon} size={18} style={{ color: quick.color }} />
                    <p className="font-semibold text-card-foreground">{quick.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    About {quick.seconds < 120 ? `${quick.seconds} seconds` : `${Math.round(quick.seconds / 60)} minutes`}
                  </p>
                  <ol className="text-sm space-y-1.5 list-decimal list-inside text-muted-foreground flex-1">
                    {quick.steps.map((step, idx) => (
                      <li key={idx}>
                        <span className="text-foreground">{step}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4">
                    <Button variant="outline" size="sm" onClick={() => navigate(quick.path)}>
                      <Icon name="ArrowRight" size={14} />
                      {quick.ctaLabel}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Six functions reference ------------------------------------ */}
          <section className="bg-card border border-border rounded-lg p-6 print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Brain" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                Functions of behavior (quick reference)
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Same behavior can have different functions in different students. <em>Same
              behavior, different function = opposite intervention.</em> This is arguably
              the single most important thing to get right when logging incidents.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {FUNCTIONS_OF_BEHAVIOR.map((fn) => (
                <div
                  key={fn.value}
                  className="rounded-lg border p-3"
                  style={{ borderColor: `${fn.color}55`, backgroundColor: `${fn.color}0d` }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name={fn.icon} size={16} style={{ color: fn.color }} />
                    <p className="font-semibold text-card-foreground">{fn.label}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{fn.description}</p>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/pbis-resources')}
              >
                <Icon name="BookOpen" size={14} />
                Full PBIS Resources reference
              </Button>
            </div>
          </section>

          {/* FAQ -------------------------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6 print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="HelpCircle" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                Frequently asked questions
              </h2>
            </div>
            <div className="divide-y divide-border">
              {FAQ_ITEMS.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <button
                    key={item.q}
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full text-left py-3 px-1 focus:outline-none focus:ring-2 focus:ring-primary rounded"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-medium text-card-foreground">{item.q}</p>
                      <Icon
                        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                        size={16}
                        className="text-muted-foreground flex-shrink-0 mt-1"
                      />
                    </div>
                    {isOpen && (
                      <p className="text-sm text-muted-foreground mt-2">{item.a}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Glossary --------------------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6 print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="BookMarked" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">Glossary</h2>
            </div>
            <div className="mb-4">
              <Input
                label="Search glossary"
                placeholder="e.g. ABC, function, MTSS, fidelity…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {glossaryFiltered.length === 0 ? (
              <p className="text-sm text-muted-foreground">No matches. Try a different search.</p>
            ) : (
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {glossaryFiltered.map((item) => (
                  <div key={item.term} className="border-l-2 border-primary/40 pl-3">
                    <dt className="font-semibold text-card-foreground">{item.term}</dt>
                    <dd className="text-muted-foreground text-sm">{item.def}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          {/* Full docs links -------------------------------------------- */}
          {/*
            These three cards used to be <a href="https://github.com/.../
            blob/main/docs/*.md"> external links. The repo is private, so
            every visitor hit a GitHub 404. We now bundle the markdown
            into the app itself (?raw import + marked) and open it in an
            inline viewer — no round-trip to GitHub, works for demo
            users, works for signed-in users, works when the customer's
            repo is private OR public.
          */}
          <section className="bg-card border border-border rounded-lg p-6 print:hidden">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="FileText" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">Full written docs</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Tap a card to open the full guide right here inside the app — no download or GitHub account needed.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {['user_manual', 'quick_start', 'admin_guide'].map((docKey) => {
                const meta = BUNDLED_DOCS[docKey];
                return (
                  <button
                    key={meta.key}
                    type="button"
                    onClick={() => setOpenDoc(meta.key)}
                    className="text-left rounded-lg border border-border p-4 hover:border-primary hover:bg-primary/5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={meta.icon} size={16} className="text-primary" />
                      <p className="font-semibold text-card-foreground">{meta.title}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{meta.subtitle}</p>
                  </button>
                );
              })}
            </div>

            {/* Owner-only: District cheat sheet card. Rendered ONLY for
                real Supabase-authed owners so demo evaluators never
                see the sales playbook that's meant for the founder
                to consult during their pitch. */}
            {isRealOwner && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Owner only
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/cheat-sheet')}
                  className="w-full text-left rounded-lg border border-primary/30 bg-primary/5 p-4 hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="BookMarked" size={16} className="text-primary" />
                    <p className="font-semibold text-card-foreground">
                      District Q&amp;A Cheat Sheet
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Your live-call playbook — safe-to-say answers for
                    every question districts ask, plus what NOT to say.
                    Keep it open on your phone during pitches.
                  </p>
                </button>
              </div>
            )}
          </section>

          {/* Printable quick-start card -------------------------------- */}
          {/*
            This section is styled to look good on paper. Everything else
            has `print:hidden`; this block appears on screen too, but is
            the ONLY thing that prints when the user hits "Print Quick
            Start Card." Result: hit Print -> browser dialog -> one clean
            page of the most important reference material.
          */}
          <section className="bg-card border border-border rounded-lg p-6 print:border-0 print:p-0 print:bg-transparent print:shadow-none">
            <div className="flex items-center gap-2 mb-4 print:hidden">
              <Icon name="Printer" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">
                Printable Quick Start Card
              </h2>
              <span className="text-xs text-muted-foreground ml-auto">
                Tap Print at the top of the page to send just this card to the printer.
              </span>
            </div>

            <div className="print:block">
              <div className="border-b border-border pb-2 mb-3">
                <h3 className="text-lg font-bold text-foreground">
                  EduTracker Pro — Quick Start Card
                </h3>
                <p className="text-xs text-muted-foreground">
                  A data-tracking platform for special education • Print & keep at your workstation
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="border border-border rounded p-3">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-primary mb-1">
                    Four data streams
                  </p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li><strong>Students</strong> — who</li>
                    <li><strong>IEP Goals</strong> — what you’re working on</li>
                    <li><strong>Behavior Incidents (ABC)</strong> — what happened</li>
                    <li><strong>Behavior Support Plans</strong> — how the team responds</li>
                  </ol>
                </div>

                <div className="border border-border rounded p-3">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-primary mb-1">
                    Log incident (60 sec)
                  </p>
                  <p><strong>A</strong> — Location, activity, trigger.</p>
                  <p><strong>B</strong> — Type, operational description, intensity 1–5, duration.</p>
                  <p><strong>C</strong> — Adult response + reaction (stopped/continued/intensified).</p>
                  <p><strong>Function</strong> — attention / escape / tangible / sensory / automatic / unknown.</p>
                </div>

                <div className="border border-border rounded p-3">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-primary mb-1">
                    SMART goal template
                  </p>
                  <p><strong>S</strong>pecific — what will change</p>
                  <p><strong>M</strong>easurable — frequency / duration / %</p>
                  <p><strong>A</strong>chievable — realistic vs baseline</p>
                  <p><strong>R</strong>elevant — tied to goal area</p>
                  <p><strong>T</strong>ime-bound — mastery + target date</p>
                </div>

                <div className="border border-border rounded p-3">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-primary mb-1">
                    Functions of behavior
                  </p>
                  <p><strong>Attention</strong> → teach "ask for attention"</p>
                  <p><strong>Escape</strong> → teach "I need a break"</p>
                  <p><strong>Tangible</strong> → teach "ask / wait for turn"</p>
                  <p><strong>Sensory</strong> → structured sensory breaks</p>
                  <p><strong>Automatic</strong> → substitute + medical review</p>
                  <p><strong>Unknown</strong> → keep logging + review analytics</p>
                </div>

                <div className="border border-success/40 bg-success/5 rounded p-3">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-success mb-1">
                    Escalation — DO
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Stay calm. Fewer words. Calm tone.</li>
                    <li>Safety first.</li>
                    <li>Give space, 2–3 min minimum.</li>
                    <li>Follow the BIP if one exists.</li>
                  </ul>
                </div>

                <div className="border border-destructive/40 bg-destructive/5 rounded p-3">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-destructive mb-1">
                    Escalation — DON'T
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Argue, debate, negotiate.</li>
                    <li>Match tone / volume.</li>
                    <li>Corner or crowd the student.</li>
                    <li>Take it personally.</li>
                  </ul>
                </div>

                <div className="border border-border rounded p-3 md:col-span-2">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-primary mb-1">
                    Behavior-specific praise formula
                  </p>
                  <p>
                    <em>Generic:</em> "Good job!" → <em>Specific:</em> "Nice work
                    waiting your turn quietly."
                  </p>
                  <p className="mt-1"><strong>Formula:</strong> [praise opener] + [exactly what they did].</p>
                </div>

                <div className="border border-border rounded p-3 md:col-span-2">
                  <p className="font-bold uppercase tracking-wide text-[10px] text-primary mb-1">
                    Live vs Demo mode
                  </p>
                  <p>
                    <strong className="text-success">Live sync</strong> = your data is
                    saved to the cloud and shared with the team.{' '}
                    <strong className="text-warning">Demo mode</strong> = data lives only
                    in this browser. If you’re supposed to be Live and see Demo, tell
                    your admin.
                  </p>
                </div>
              </div>

              <p className="text-[10px] text-muted-foreground mt-3 print:mt-2">
                EduTracker Pro • Quick Start Card v1.0 • See Sidebar → Help & Manual for full docs.
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Print-mode CSS. Everything on-screen but the quick-start card is
          `print:hidden`. This section defines page-level print styles. */}
      <style>{`
        @media print {
          html, body { background: white !important; }
          @page { margin: 0.5in; }
        }
      `}</style>

      {/* Bundled-doc viewer.
          Renders the selected markdown doc (Quick Start Card, User Manual,
          Admin Guide) in an accessible full-screen panel. The markdown is
          compiled at render time via `marked` from the ?raw import at the
          top of this file, so there's no dependency on the source repo
          being reachable at github.com. */}
      {openDocMeta && (
        <div
          className="fixed inset-0 z-[1100] flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/60 print:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bundled-doc-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenDoc(null);
          }}
        >
          <div className="bg-card w-full max-w-4xl max-h-full sm:max-h-[92vh] rounded-none sm:rounded-lg shadow-elevated flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 border-b border-border px-4 py-3 sticky top-0 bg-card z-10">
              <Icon name={openDocMeta.icon} size={20} className="text-primary" />
              <h2 id="bundled-doc-title" className="text-lg font-semibold text-card-foreground flex-1 truncate">
                {openDocMeta.title}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                title="Print this document"
              >
                <Icon name="Printer" size={14} />
                <span className="hidden sm:inline">Print</span>
              </Button>
              <Button
                ref={docCloseRef}
                variant="ghost"
                size="icon"
                onClick={() => setOpenDoc(null)}
                aria-label="Close document"
              >
                <Icon name="X" size={18} />
              </Button>
            </div>
            <div
              className="prose prose-sm sm:prose-base max-w-none px-4 sm:px-6 py-5 overflow-y-auto flex-1 prose-headings:scroll-mt-4 prose-table:text-sm prose-table:border prose-th:border prose-th:bg-muted/40 prose-th:px-2 prose-th:py-1 prose-td:border prose-td:px-2 prose-td:py-1 prose-a:text-primary hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: openDocHtml }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpAndManualPage;
